import { useCallback, useState, useEffect, useMemo } from "react";
import {
  handleError,
  parseNumber,
  sortAlphabetically,
  validateEmail,
  validateNumber,
} from "utils";
import produce from "immer";
import {
  createSendbirdUser,
  getExternalMembers,
  useGetStaffMembers,
} from "repositories/chat-repository";
import { useOwnerSpecificStaffDesignation } from "repositories/scheduler-repository";
import {
  getFullName,
  inBytes,
  mergeStaffListRowsByUserId,
} from "Messenger/pages/TeamConversation/utils";
import { useOffices } from "repositories/office-repository";
import { debounce, uniqBy } from "lodash";
import constants from "../../constants";
import {
  useAppointedPatients,
  useOfficesPatientChat,
} from "repositories/patient-repository";
import { useQueryClient } from "react-query";
import { handleCallError } from "Messenger/calling/sendbird-calls/SbCallsContext/statefy";
import useOfficeContextOfficeList from "hooks/useOfficeContextOfficeList";

const OFFICE_PAGE_SIZE = 30;
const STAFF_PAGE_SIZE = 30;

const allowedTypes = constants.chat.allowedTypesForGroupImage;
export const defaultOffice = { value: 0, label: "All Offices" };
const defaultRole = { value: 0, label: "All Roles" };

const pickDesignationIdsForChannelMember = (entity) => {
  if (!entity) return [];
  if (Array.isArray(entity.designationIds) && entity.designationIds.length) {
    return [
      ...new Set(
        entity.designationIds
          .map((x) => Number(x))
          .filter((n) => !Number.isNaN(n)),
      ),
    ];
  }
  const raw =
    entity.designationId ??
    entity.designation?.id ??
    entity.designation?.designationId;
  if (raw != null && raw !== "") {
    const n = Number(raw);
    return Number.isNaN(n) ? [] : [n];
  }
  return [];
};

const useChannelListHeaderState = (dependencies) => {
  const {
    setChannelListQuery,
    setCurrentChannel,
    filteredOffices,
    setFilteredOffices,
    selectedOwner,
    sdk,
    currentUser,
    externalTabActive,
    t,
    updateChannelList,
    channel,
    isPatient = false,
    filteredOfficesForNew,
    doctorChatCheck = false,
    setFilteredRoleIds,
  } = dependencies;
  const queryClient = useQueryClient();

  //For new patient conversation
  const [addingLoader, setAddingLoader] = useState();
  const [patientList, setPatientList] = useState([]);
  const [currentPatientPage, setCurrentPatientPage] = useState(1);
  const [totalPatientPages, setTotalPatientPages] = useState(1);
  const [isPatientConversationPopOpen, setIsPatientConversationPopOpen] =
    useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchPatient, setSearchPatient] = useState("");
  const [debouncedPatientSearch, setDebouncedPatientSearch] = useState("");

  /*-------------------------------------------------------------------*/
  //State Internal chat creation
  const [isNewGroupPopupOpen, setIsNewGroupPopupOpen] = useState(false);
  const [isCreateGroupPopupOpen, setIsCreateGroupPopupOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState(defaultOffice);
  const [searchText, setSearchText] = useState("");
  const [debounceSearchText, setDebounceSearchText] = useState("");
  const [groupImage, setGroupImage] = useState(null);
  const [groupImageUrl, setGroupImageUrl] = useState("");
  const [groupName, setGroupName] = useState("");

  /*-------------------------------------------------------------------*/
  //State for external chat creation
  const [isNewConversationPopupOpen, setIsNewConversationPopupOpen] =
    useState(false);
  const [
    isNewConversationOfficePopupOpen,
    setIsNewConversationOfficePopupOpen,
  ] = useState(false);
  const [conversationTitle, setConversationTitle] = useState("");
  const [findByEmail, setFindByEmail] = useState(true);
  const [searchEmail, setSearchEmail] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [searchedExternalMembers, setSearchedExternalMembers] = useState([]);
  const [loadingExternalMembers, setLoadingExternalMembers] = useState(false);
  const [showShareApp, setShowShareApp] = useState(false);
  const [isCallHistoryModalOpen, setCallHistoryModalOpen] = useState(false);

  const toggleCallHistoryPopUp = () =>
    setCallHistoryModalOpen(!isCallHistoryModalOpen);

  /*-------------------------------------------------------------------*/
  //Commont state
  const [dropdownOfficeOpen, setDropdownOfficeOpen] = useState(false);
  const [dropdownChatOpen, setDropdownChatOpen] = useState(false);
  const [errors, setErrors] = useState({
    groupImage: "",
    groupName: "",
    externalGroupTitle: "",
    externalChatEmail: "",
  });
  const [actionInProgress, setActionInProgress] = useState(false);

  //For officeList
  const [officeList, setOfficeList] = useState([]);
  const [currentOfficePage, setCurrentOfficePage] = useState(1);
  const [totalOfficePages, setTotalOfficePages] = useState(1);
  const [selectedOffices, setSelectedOffices] = useState([]);
  const [selectedDesignations, setSelectedDesignations] = useState([]);

  const [officeListForPatient, setOfficeListForPatient] = useState([]);
  const [currentOfficePageForPatient, setCurrentOfficePageForPatient] =
    useState(1);
  const [totalOfficePagesForPatient, setTotalOfficePagesForPatient] =
    useState(1);

  //For stafflist
  const [staffList, setStaffList] = useState([]);
  const [currentStaffPage, setCurrentStaffPage] = useState(1);
  const [totalStaffPages, setTotalStaffPages] = useState(1);

  /*-------------------------------------------------------------------*/
  //** Common Methods */
  const toggleOffice = () => setDropdownOfficeOpen((prevState) => !prevState);
  const toggleChat = () => setDropdownChatOpen((prevState) => !prevState);
  const showArchivedChats = () => {
    setChannelListQuery(
      produce((draft) => {
        draft.channelListQuery.hiddenChannelFilter = constants.chat.archived;
      }),
    );
  };
  const showUnarchivedChats = () => {
    setChannelListQuery(
      produce((draft) => {
        draft.channelListQuery.hiddenChannelFilter = constants.chat.unArchived;
      }),
    );
  };

  const loadMoreStaff = () => {
    if (currentStaffPage < totalStaffPages) {
      setCurrentStaffPage((prev) => prev + 1);
    }
  };

  const loadMoreOffices = () => {
    if (currentOfficePage < totalOfficePages) {
      setCurrentOfficePage((prev) => prev + 1);
    }
  };
  const loadMoreOfficesForPatientChat = () => {
    if (currentOfficePageForPatient < totalOfficePagesForPatient) {
      setCurrentOfficePageForPatient((prev) => prev + 1);
    }
  };

  const loadMorePatientAppoinments = () => {
    if (currentPatientPage < totalPatientPages) {
      setCurrentPatientPage((prev) => prev + 1);
    }
  };

  const selectedOfficesLabel = (selectedOption) => {
    const selectedCount = selectedOption.length;
    const value =
      selectedCount > 1
        ? `${selectedCount} items selected`
        : (selectedOption?.[0]?.label ?? "");
    return value || t("messenger.allOffices");
  };

  const selectedRolesLabel = (selectedOption) => {
    const selectedCount = selectedOption.length;
    const value =
      selectedCount > 1
        ? `${selectedCount} items selected`
        : (selectedOption?.[0]?.label ?? "");
    return value || t("accountOwner.allRoles");
  };

  /*-------------------------------------------------------------------*/
  useEffect(() => {
    //Reset the staff and office data lists if selected owner has changed.
    setStaffList([]);
    setCurrentStaffPage(1);
    setTotalStaffPages(1);

    setOfficeList([]);
    setCurrentOfficePage(1);
    setTotalOfficePages(1);
    setSelectedOffices([]);
    setSelectedDesignations([]);
    setSearchText("");
    setDebounceSearchText("");

    setOfficeListForPatient([]);
    setCurrentOfficePageForPatient(1);
    setTotalOfficePagesForPatient(1);
  }, [selectedOwner]);

  //** Data from APIs */
  const enabled = !!selectedOwner?.id;
  const {
    isOfficeNavigationContext,
    officeList: contextOfficeList,
    isLoading: isContextOfficeListLoading,
  } = useOfficeContextOfficeList(OFFICE_PAGE_SIZE);
  /* Get Active Offices */
  const {
    data: activeOffices,
    isLoading: activeOfficesIsLoading,
    isFetching: activeOfficesIsFetching,
    isRefetching: activeOfficesIsRefetching,
  } = useOffices(selectedOwner?.id, currentOfficePage, OFFICE_PAGE_SIZE, true, {
    enabled: enabled && !isOfficeNavigationContext,
  });
  const {
    data: activeOfficesForPatientChat,
    isLoading: activeOfficesForPatientChatIsLoading,
    isFetching: activeOfficesForPatientChatIsFetching,
    isRefetching: activeOfficesForPatientChatIsRefetching,
  } = useOfficesPatientChat(
    currentOfficePage,
    OFFICE_PAGE_SIZE,
    selectedOwner?.id,
    { enabled: !!isPatient && !doctorChatCheck && !isOfficeNavigationContext },
  );
  const isLoadingOfficesList = useMemo(
    () =>
      isContextOfficeListLoading ||
      activeOfficesIsLoading ||
      activeOfficesIsFetching ||
      activeOfficesIsRefetching ||
      activeOfficesForPatientChatIsLoading ||
      activeOfficesForPatientChatIsFetching ||
      activeOfficesForPatientChatIsRefetching,
    [
      isContextOfficeListLoading,
      activeOfficesIsLoading,
      activeOfficesIsFetching,
      activeOfficesIsRefetching,
      activeOfficesForPatientChatIsLoading,
      activeOfficesForPatientChatIsFetching,
      activeOfficesForPatientChatIsRefetching,
    ],
  );
  useEffect(() => {
    if (activeOfficesForPatientChat) {
      return () => {
        queryClient.invalidateQueries(["/Doctor/DoctorOfficesUnderOwner"]);
      };
    }
  }, []);

  useEffect(() => {
    if (isOfficeNavigationContext) {
      setOfficeList(contextOfficeList);
      setTotalOfficePages(1);
      return;
    }
    if (activeOffices) {
      setOfficeList((prev) => uniqBy([...prev, ...activeOffices.items], "id"));
      setTotalOfficePages(activeOffices?.pagination?.totalPages);
    }
  }, [activeOffices, isOfficeNavigationContext, contextOfficeList]);

  useEffect(() => {
    if (isOfficeNavigationContext) {
      setOfficeListForPatient(contextOfficeList);
      setTotalOfficePagesForPatient(1);
      return;
    }
    if (activeOfficesForPatientChat) {
      setOfficeListForPatient((prev) =>
        uniqBy([...prev, ...activeOfficesForPatientChat.data], "id"),
      );
      setTotalOfficePagesForPatient(
        activeOfficesForPatientChat?.pagination?.totalPages,
      );
    }
  }, [activeOfficesForPatientChat, isOfficeNavigationContext, contextOfficeList]);

  const { data: ownerSpecificDesignationData } =
    useOwnerSpecificStaffDesignation(
      { selectedId: selectedOwner?.id },
      { enabled },
    );

  const offices = useMemo(() => {
    return sortAlphabetically(
      officeList.map((off) => ({ value: off.id, label: off.name })),
      "label",
    );
  }, [officeList]);

  const officesForPatientChat = useMemo(() => {
    return sortAlphabetically(
      officeListForPatient.map((off) => ({ value: off.id, label: off.name })),
      "label",
    );
  }, [officeListForPatient]);

  const designations = useMemo(() => {
    const roles = ownerSpecificDesignationData?.designations_list || [];
    const options = roles.map((role) => ({
      value: role.id,
      label: role.name || role.title || "",
    }));

    return [
      {
        ...defaultRole,
        label: t("accountOwner.allRoles"),
      },
      ...sortAlphabetically(options, "label"),
    ];
  }, [ownerSpecificDesignationData, t]);

  /* Get Staff members */
  const { isLoading: isLoadingStaffList, data: staffData } = useGetStaffMembers(
    selectedOwner?.id,
    selectedOffices?.length
      ? selectedOffices.reduce((prev, curr) => {
          if (curr.value) {
            prev.push(curr.value);
          }
          return prev;
        }, [])
      : selectedOffice.value === 0
        ? []
        : [selectedOffice.value],
    selectedDesignations?.length
      ? selectedDesignations.reduce((prev, curr) => {
          if (curr.value) {
            prev.push(curr.value);
          }
          return prev;
        }, [])
      : [],
    debounceSearchText,
    currentStaffPage,
    STAFF_PAGE_SIZE,
    { enabled },
  );

  // Get Appointed Patient
  const {
    isLoading: isPatientListLoading,
    data: patientData,
    mutate,
  } = useAppointedPatients();
  useEffect(() => {
    if (!isPatient || !selectedOwner?.id || isPatientListLoading) return;
    const params = {
      searchTerm: debouncedPatientSearch,
      pageNumber: currentPatientPage,
      pageSize: 10,
      ownerId: selectedOwner?.id,
    };
    const list = isPatientConversationPopOpen
      ? filteredOfficesForNew
      : filteredOffices;
    if (list[0] !== 0) {
      params.officeIds = list;
    }
    mutate(params);
  }, [
    filteredOfficesForNew,
    isPatient,
    debouncedPatientSearch,
    currentPatientPage,
    selectedOwner,
    filteredOffices,
    mutate,
    isPatientConversationPopOpen,
  ]);

  useEffect(() => {
    if (!patientData) return;
    if (currentPatientPage === 1) {
      setPatientList(patientData.data);
    } else {
      setPatientList((prev) => [...prev, ...patientData.data]);
    }
    setTotalPatientPages(patientData.pagination.totalPages);
  }, [patientData, debouncedPatientSearch, filteredOffices, totalPatientPages]);

  useEffect(() => {
    if (!staffData) return;
    const pageRows = staffData.data || [];
    if (currentStaffPage === 1) {
      setStaffList(mergeStaffListRowsByUserId(pageRows));
    } else {
      setStaffList((prev) =>
        mergeStaffListRowsByUserId([...prev, ...pageRows]),
      );
    }
    setTotalStaffPages(staffData?.pagination?.totalPages ?? 1);
  }, [staffData]);

  const staffMembers = useMemo(() => {
    return (staffList || []).map((item) => {
      const {
        userId,
        profilePic,
        emailId,
        contactNumber,
        isAccountOwner,
        officeContactNumber,
        userOffice,
      } = item;
      const officeIds = Array.isArray(item.officeIds)
        ? item.officeIds
        : item.officeId != null && item.officeId !== ""
          ? [Number(item.officeId)].filter((n) => !Number.isNaN(n))
          : [];
      const primaryOfficeId = officeIds[0] ?? item.officeId ?? null;
      const designationIds = pickDesignationIdsForChannelMember(item);
      return {
        id: userId,
        dbProfilePic: profilePic,
        profilePic:
          profilePic || require("assets/images/staff-default-rounded.png"),
        name: getFullName(item),
        emailId,
        officeName: null,
        officeId: primaryOfficeId,
        officeIds,
        designationIds,
        designationId: designationIds[0] ?? null,
        isAccountOwner,
        contactNumber: contactNumber || officeContactNumber,
        userOffice,
      };
    });
  }, [staffList]);

  /* -------------------------------------------------------------------------------------------------------------- */
  //Internal Chat creation - for step 1
  const isThisUserSelected = (user, isPatient = false) => {
    const ids = (item) => `${item?.patient?.id}-${item?.office?.id}`;
    if (isPatient) {
      const inId = selectedPatient ? ids(selectedPatient) : "";
      const tgId = ids(user);
      return inId === tgId;
    }
    const index = selectedStaff.findIndex((u) => u.id === user.id);
    return index > -1 ? true : false;
  };

  const handleSelectPatient = (_, patient) => {
    setSelectedPatient(patient);
  };

  const handleSelectStaff = (e, staff) => {
    const checked = e.target.checked;
    if (checked) {
      setSelectedStaff((prev) => {
        prev.push(staff);
        return [...prev];
      });
    } else {
      setSelectedStaff((prev) => prev.filter((s) => s.id !== staff.id));
    }
  };

  const disableOtherUsers = () => false;

  const handleSelectOffice = (office) => {
    setSelectedStaff([]);
    setPatientList([]);
    setSelectedOffice(office);
  };

  const handleSearchText = useCallback((e) => {
    const value = e.target.value;
    setSearchText(value);
  }, []);
  const handleDebouncedPatientSearch = useCallback(
    debounce((value) => {
      setDebouncedPatientSearch(value);
    }, 1000),
    [],
  );
  const handleDebouncedStaffSearch = useCallback(
    debounce((value) => {
      setDebounceSearchText(value);
    }, 1000),
    [],
  );

  const searchStaffHandler = (value) => {
    setCurrentStaffPage(1);
    setTotalStaffPages(1);
    setSearchText(value);
    handleDebouncedStaffSearch(value);
  };

  const handleOfficeFilterForStaff = (incomingSelected) => {
    const isAllOffices = incomingSelected.value === defaultOffice.value;

    if (isAllOffices) {
      if (!selectedOffices.length) {
        return;
      }
      setStaffList([]);
      setCurrentStaffPage(1);
      setTotalStaffPages(1);
      setSelectedOffices([]);
      return;
    }

    setStaffList([]);
    setCurrentStaffPage(1);
    setTotalStaffPages(1);
    const index = selectedOffices.findIndex(
      (item) => item.value === incomingSelected.value,
    );
    let selected = [...selectedOffices];
    if (index > -1) {
      selected.splice(index, 1);
    } else {
      selected.push(incomingSelected);
    }
    setSelectedOffices(selected);
  };

  const handleDesignationFilterForStaff = (incomingSelected) => {
    const isAllRoles = incomingSelected.value === defaultRole.value;

    if (isAllRoles) {
      if (!selectedDesignations.length) {
        return;
      }
      setStaffList([]);
      setCurrentStaffPage(1);
      setTotalStaffPages(1);
      setSelectedDesignations([]);
      return;
    }

    setStaffList([]);
    setCurrentStaffPage(1);
    setTotalStaffPages(1);
    const index = selectedDesignations.findIndex(
      (item) => item.value === incomingSelected.value,
    );
    let selected = [...selectedDesignations];
    if (index > -1) {
      selected.splice(index, 1);
    } else {
      selected.push(incomingSelected);
    }
    setSelectedDesignations(selected);
  };

  const resetStaffListFilterStates = () => {
    setCurrentOfficePage(1);
    setTotalOfficePages(1);
    setSelectedOffices([]);
    setSelectedDesignations([]);
    setSearchText("");
    setDebounceSearchText("");
  };

  const searchPatientHandler = (value) => {
    setCurrentPatientPage(1);
    setTotalPatientPages(1);
    setSearchPatient(value);
    handleDebouncedPatientSearch(value);
  };

  const closeStepOneModal = () => {
    setCurrentPatientPage(1);
    setTotalPatientPages(1);
    setSelectedStaff([]);
    setSearchText("");
    setSearchPatient("");
    setPatientList([]);
    setSelectedOffice(defaultOffice);
    setIsNewGroupPopupOpen(false);
    setIsPatientConversationPopOpen(false);
    setSelectedPatient(null);
  };

  const handleStepOne = async (isPatient = false) => {
    if (isPatient && selectedPatient) {
      handleOne2OnePatientChatCreation();
      return;
    }
    if (!selectedStaff.length) return;
    if (selectedStaff.length > 1) {
      setIsNewGroupPopupOpen(false);
      setIsCreateGroupPopupOpen(true);
    } else {
      setActionInProgress(true);
      await handleOne2OneChatCreation();
      setActionInProgress(false);
    }
  };
  async function handleOne2OneChatCreation() {
    if (!sdk) return;
    if (addingLoader) return;
    setAddingLoader(true);
    try {
      const selectedUser = selectedStaff?.[0];
      await ensureSendbirdUser(selectedUser);

      const existingChannel = await findExistingChannel(selectedUser.id);

      if (existingChannel) {
        setAndCloseChannel(existingChannel);
        setAddingLoader(false);
        return;
      }

      const channel = await createNewChannel(
        selectedUser,
        currentUser,
        externalTabActive,
        selectedOwner,
      );

      setAndCloseChannel(channel);
    } catch (err) {
      handleCallError(err);
    } finally {
      setAddingLoader(false);
    }
  }

  async function ensureSendbirdUser({ id, name, dbProfilePic }) {
    const query = sdk.createApplicationUserListQuery({
      limit: 1,
      userIdsFilter: [id.toString()],
    });
    const users = await query.next();
    if (!users.length) {
      await createSendbirdUser({
        user_id: id.toString(),
        nickname: name,
        profile_url: dbProfilePic || "",
      });
    }
  }

  async function ensureSendbirdUsersExist(staffItems = []) {
    if (!sdk || !staffItems.length) return;
    const userListQuery = sdk.createApplicationUserListQuery({
      limit: staffItems.length,
      userIdsFilter: staffItems.map((member) => member.id.toString()),
    });
    const sendbirdUsers = await userListQuery.next();
    if (sendbirdUsers.length === staffItems.length) return;

    const notSendbirdUsers = staffItems.filter(
      (member) =>
        !sendbirdUsers.some((sendbirdUser) => member.id == sendbirdUser.userId),
    );
    await Promise.all(
      notSendbirdUsers.map((user) =>
        createSendbirdUser({
          user_id: user.id?.toString(),
          nickname: user.name,
          profile_url: user.dbProfilePic || "",
        }),
      ),
    );
  }

  async function findExistingChannel(SU_id) {
    const query = sdk.groupChannel.createMyGroupChannelListQuery({
      limit: 100,
      includeEmpty: true,
      includeFrozen: false,
      customTypesFilter: [
        externalTabActive ? constants.chat.external : constants.chat.internal,
      ],
      hiddenChannelFilter: constants.chat.unArchived,
      userIdsFilter: {
        userIds: [SU_id.toString()],
        includeMode: false,
        queryType: "AND",
      },
    });

    const channels = await query.next();
    return channels.find((ch) => channelHasMember(ch, SU_id));
  }

  function channelHasMember(channel, SU_id) {
    if (!channel.data) return false;
    let data;
    try {
      data = JSON.parse(channel.data);
    } catch {
      return false;
    }
    return (
      Array.isArray(data.members) && data.members.some((m) => m.id == SU_id)
    );
  }

  async function createNewChannel(
    selectedUser,
    currentUser,
    externalTabActive,
    selectedOwner,
  ) {
    const params = buildGroupChannelParams(
      selectedUser,
      currentUser,
      externalTabActive,
      selectedOwner,
    );
    return sdk.groupChannel.createChannel(params);
  }

  function buildGroupChannelParams(
    selectedUser,
    currentUser,
    externalTabActive,
    selectedOwner,
  ) {
    const {
      id: SU_id,
      emailId: SU_emailId,
      name: SU_name,
      isAccountOwner: SU_isAccountOwner,
      officeId: suPrimaryOfficeId,
      officeIds: suOfficeIdsRaw,
      contactNumber: SU_contactNumber,
      userOffice,
    } = selectedUser;

    const {
      id: CU_id,
      emailId: CU_emailId,
      isAccountOwner: CU_isAccountOwner,
      isStaff: CU_isStaff,
      contactNumber: CU_contactNumber,
    } = currentUser;

    const cuOfficeIds = (() => {
      if (
        Array.isArray(currentUser.officeIds) &&
        currentUser.officeIds.length
      ) {
        return [
          ...new Set(
            currentUser.officeIds
              .map((x) => Number(x))
              .filter((n) => !Number.isNaN(n)),
          ),
        ];
      }
      if (currentUser.officeId != null && currentUser.officeId !== "") {
        const n = Number(currentUser.officeId);
        return Number.isNaN(n) ? [] : [n];
      }
      return [];
    })();

    const data = {
      officeIds: [],
      designationIds: [],
      members: [
        {
          id: SU_id,
          emailId: SU_emailId,
          officeName: null,
          name: SU_name,
          isAccountOwner: SU_isAccountOwner,
          contactNumber: SU_contactNumber,
        },
        {
          id: CU_id,
          emailId: CU_emailId,
          officeName: null,
          name: getFullName(currentUser),
          isAccountOwner: CU_isAccountOwner,
          contactNumber: CU_contactNumber,
        },
      ],
    };
    (userOffice || []).forEach(({ officeId, designationId }) => {
      if (officeId && !data.officeIds.includes(officeId)) {
        data.officeIds.push(officeId);
      }
      if (designationId && !data.designationIds.includes(designationId)) {
        data.designationIds.push(designationId);
      }
    });
    if (!CU_isAccountOwner && CU_isStaff) {
      data.accountOwner = {
        id: selectedOwner.id,
        name: getFullName(selectedOwner),
        emailId: selectedOwner.emailId,
      };
    } else if (CU_isAccountOwner && !CU_isStaff) {
      data.accountOwner = {
        id: CU_id,
        name: getFullName(currentUser),
        emailId: CU_emailId,
      };
    }
    return {
      invitedUserIds: [SU_id.toString()],
      name: `${SU_name} and ${getFullName(currentUser)}`,
      operatorUserIds: [CU_id.toString(), SU_id.toString()],
      customType: externalTabActive
        ? constants.chat.external
        : constants.chat.internal,
      data: JSON.stringify(data),
    };
  }

  function setAndCloseChannel(channel) {
    setCurrentChannel(channel);
    updateChannelList();
    closeStepOneModal();
  }

  async function handleOne2OnePatientChatCreation() {
    if (sdk) {
      if (addingLoader) return;
      setAddingLoader(true);
      try {
        const selectedUser = selectedPatient;

        const SU_id = selectedUser?.patient?.id || "";
        const name = getFullName(selectedUser.patient);
        const officeId = selectedUser?.office?.id || "";
        const profilePic = selectedUser?.patient?.profilePic || "";
        const SU_emailId = selectedUser?.patient?.emailId || "";
        const SU_contactNumber = selectedUser?.patient?.contactNumber || "";
        const officeName = selectedUser?.office?.name || "";

        selectedUser.name = getFullName(selectedUser.patient);
        //Check if selected user has active acc in sendbird chat
        const userListQueryParam = {
          limit: 1,
          userIdsFilter: [SU_id?.toString()],
        };
        const userListQuery =
          sdk.createApplicationUserListQuery(userListQueryParam);
        const sendbirdUser = await userListQuery.next();
        if (!sendbirdUser.length) {
          const userData = {
            user_id: SU_id?.toString(),
            nickname: name,
            profile_url: profilePic || "",
          };

          await createSendbirdUser(userData);
        } else {
          //Check if there is already any chat initiated with this selected user
          const channelListQuery =
            sdk.groupChannel.createMyGroupChannelListQuery({
              limit: 100,
              includeEmpty: true,
              includeFrozen: false,
              customTypesFilter: [
                constants.chat.patient.open,
                constants.chat.patient.close,
              ],
              hiddenChannelFilter: constants.chat.unArchived,
              userIdsFilter: {
                userIds: [SU_id?.toString()],
                includeMode: false,
                queryType: "AND",
              },
            });
          const channelList = await channelListQuery.next();
          if (channelList.length) {
            const foundChannel = channelList.find((chnl) => {
              let { data: chnlData } = chnl;
              if (chnlData) {
                chnlData = JSON.parse(chnlData);
                if (chnlData?.members?.length) {
                  const idx = chnlData?.members?.findIndex((mem) => {
                    return mem.id == SU_id && mem.officeId == officeId;
                  });
                  if (idx > -1) {
                    return true;
                  }
                }
              }
              return false;
            });
            if (foundChannel) {
              setCurrentChannel(foundChannel);
              closeStepOneModal();
              setAddingLoader(false);
              return;
            }
          }
        }
        const {
          id: CU_id,
          emailId: CU_emailId,
          isAccountOwner: CU_isAccountOwner,
          isStaff: CU_isStaff,
          contactNumber: CU_contactNumber,
        } = currentUser;
        const groupChannelParams = {};
        groupChannelParams.invitedUserIds = [SU_id.toString()];
        groupChannelParams.name = `${name} and ${getFullName(currentUser)}`;
        groupChannelParams.operatorUserIds = [
          CU_id?.toString(),
          SU_id?.toString(),
        ];
        groupChannelParams.customType = constants.chat.patient.open;
        const data = {};
        data.members = [
          {
            id: SU_id,
            emailId: SU_emailId,
            officeId,
            officeName,
            name,
            ownerId: 0,
            contactNumber: SU_contactNumber,
          },
          {
            id: CU_id,
            emailId: CU_emailId,
            officeId,
            officeName,
            name,
            ownerId: 0,
            contactNumber: CU_contactNumber,
          },
        ];
        if (!CU_isAccountOwner && CU_isStaff) {
          data.accountOwner = {
            id: selectedOwner.id,
            name: getFullName(selectedOwner),
            emailId: selectedOwner.emailId,
          };
        } else if (CU_isAccountOwner && !CU_isStaff) {
          data.accountOwner = {
            id: CU_id,
            name: getFullName(currentUser),
            emailId: CU_emailId,
          };
        }
        groupChannelParams.data = JSON.stringify(data);
        const channelData =
          await sdk.groupChannel.createChannel(groupChannelParams);

        setCurrentChannel(channelData);
        updateChannelList();
        closeStepOneModal();
      } catch (error) {
        if (error?.response?.data?.message) {
          handleError(new Error(error.response?.data?.message));
          return;
        }
        handleError(error);
      } finally {
        setAddingLoader(false);
      }
    }
  }

  /*-------------------------------------------------------------------*/
  // INternal Chat Creation for step 2 that is for group creation

  async function handleGroupChatCreation() {
    if (sdk) {
      try {
        await ensureSendbirdUsersExist(selectedStaff);
        const {
          id: CU_id,
          emailId: CU_emailId,
          isAccountOwner: CU_isAccountOwner,
          isStaff: CU_isStaff,
        } = currentUser;
        const groupChannelParams = {};
        groupChannelParams.invitedUserIds = selectedStaff?.map((e) =>
          e.id.toString(),
        );
        groupChannelParams.name = `${groupName.trim()}`;
        groupChannelParams.coverImage = groupImage ? groupImage : "";
        if (!groupImage) {
          groupChannelParams.coverUrl = "";
        }
        groupChannelParams.operatorUserIds = [CU_id?.toString()];
        groupChannelParams.customType = !externalTabActive
          ? constants.chat.internal
          : constants.chat.external;

        const data = {
          officeIds: [],
          designationIds: [],
        };
        const prevState = {
          officeTemp: new Map(),
          designationTemp: new Map(),
        };
        selectedStaff.forEach((curr) => {
          if (curr?.userOffice?.length) {
            curr?.userOffice?.forEach(({ officeId, designationId }) => {
              if (!prevState.officeTemp.has(officeId)) {
                data.officeIds.push(officeId);
                prevState.officeTemp.set(officeId, officeId);
              }
              if (!prevState.designationTemp.has(designationId)) {
                data.designationIds.push(designationId);
                prevState.designationTemp.set(designationId, designationId);
              }
            });
          }
        });

        data.members = [
          ...selectedStaff?.map((e) => {
            return {
              id: e.id,
              emailId: e.emailId,
              officeId: e.officeId,
              officeName: e.officeName,
              name: e.name,
              isAccountOwner: e.isAccountOwner,
            };
          }),

          {
            id: CU_id,
            emailId: CU_emailId,
            officeName: null,
            name: getFullName(currentUser),
            isAccountOwner: CU_isAccountOwner,
          },
        ];
        if (!CU_isAccountOwner && CU_isStaff) {
          data.accountOwner = {
            id: selectedOwner.id,
            name: getFullName(selectedOwner),
            emailId: selectedOwner.emailId,
          };
        } else if (CU_isAccountOwner && !CU_isStaff) {
          data.accountOwner = {
            id: CU_id,
            name: getFullName(currentUser),
            emailId: CU_emailId,
          };
        }
        groupChannelParams.data = JSON.stringify(data);
        const channelContent =
          await sdk.groupChannel.createChannel(groupChannelParams);

        setCurrentChannel(channelContent);
        updateChannelList();
        closeStepTwoModal();
      } catch (error) {
        if (error?.response?.data?.message) {
          handleError(new Error(error.response?.data?.message));
          return;
        }
        handleError(error);
      }
    }
  }

  const handleGroupImageChange = (e) => {
    const file = e.target.files?.[0];
    if (allowedTypes.includes(file?.type)) {
      if (file?.size < inBytes(constants.chat.chatGroupImageSizeInMbs)) {
        setGroupImage(file);
        if (file) {
          const url = URL.createObjectURL(file);
          setGroupImageUrl(url);
        }
      } else {
        setErrors({
          groupImage: t("messenger.groupImageSizeError", {
            size: constants.chat.chatGroupImageSizeInMbs,
          }),
        });
      }
    } else {
      setErrors({
        groupImage: t("messenger.groupImageTypeError"),
      });
    }
  };

  const closeStepTwoModal = () => {
    setSelectedStaff([]);
    setSearchText("");
    setSelectedOffice(defaultOffice);
    setGroupImage(null);
    setGroupImageUrl("");
    setGroupName("");
    setErrors({});
    setIsCreateGroupPopupOpen(false);
  };

  const handleGroupName = (e) => {
    const value = e.target.value;
    setGroupName(value);
    setErrors({});
  };

  const handleStepTwo = async () => {
    setErrors({});
    if (groupName && groupName.trim().length > 0) {
      setActionInProgress(true);
      await handleGroupChatCreation();
      setActionInProgress(false);
    } else {
      setErrors({
        groupName: t("messenger.groupNameExistanceError"),
      });
    }
  };

  async function handleAddMemberInGroup() {
    if (sdk) {
      const groupChannel = await sdk.groupChannel.getChannel(channel?.url);
      try {
        await ensureSendbirdUsersExist(selectedStaff);

        const invitedUserIds = selectedStaff?.map((e) => e.id.toString()) ?? [];

        const {
          id: CU_id,
          emailId: CU_emailId,
          isAccountOwner: CU_isAccountOwner,
          isStaff: CU_isStaff,
        } = currentUser;

        let existingPayload = {};
        try {
          existingPayload = groupChannel.data
            ? JSON.parse(groupChannel.data)
            : {};
        } catch {
          existingPayload = {};
        }
        const existingMembers = Array.isArray(existingPayload.members)
          ? existingPayload.members
          : [];

        const newMemberRows = (selectedStaff ?? []).map((e) => {
          const ids =
            Array.isArray(e.officeIds) && e.officeIds.length
              ? e.officeIds
              : e.officeId != null && e.officeId !== ""
                ? [Number(e.officeId)].filter((n) => !Number.isNaN(n))
                : [];
          const des = pickDesignationIdsForChannelMember(e);
          return {
            id: e.id,
            emailId: e.emailId,
            officeId: ids[0] ?? null,
            officeIds: ids,
            officeName: null,
            name: e.name,
            isAccountOwner: e.isAccountOwner,
            designationIds: des,
            designationId: des[0] ?? null,
          };
        });

        const data = {
          members: Array.from(
            new Map(
              [...existingMembers, ...newMemberRows].map((m) => [
                String(m.id),
                m,
              ]),
            ).values(),
          ),
        };

        if (existingPayload.accountOwner) {
          data.accountOwner = existingPayload.accountOwner;
        } else if (!CU_isAccountOwner && CU_isStaff) {
          data.accountOwner = {
            id: selectedOwner.id,
            name: getFullName(selectedOwner),
            emailId: selectedOwner.emailId,
          };
        } else if (CU_isAccountOwner && !CU_isStaff) {
          data.accountOwner = {
            id: CU_id,
            name: getFullName(currentUser),
            emailId: CU_emailId,
          };
        }

        await groupChannel.inviteWithUserIds(invitedUserIds);
        await groupChannel.updateChannel({
          data: JSON.stringify(data),
        });
        const latestChannel = await sdk.groupChannel.getChannel(channel.url);
        setCurrentChannel(latestChannel);
        updateChannelList();
      } catch (error) {
        if (error?.response?.data?.message) {
          handleError(new Error(error.response?.data?.message));
          return;
        }
        handleError(error);
      }
    }
  }

  const addNewMemberInExistingGroup = async () => {
    await handleAddMemberInGroup();
  };

  const handleFilterOffices = (e, id, isChecked) => {
    // checked value of the sidebar item does not update on click; it only reflects the change after the item is actually checked.
    const checked = isChecked !== undefined ? isChecked : !e.target.checked;
    setCurrentPatientPage(1);
    if (checked) {
      if (id == 0) {
        setFilteredOffices(() => {
          const updated = [id];
          localStorage.setItem(
            constants.localStorageKeys.persistOfficeFilter,
            JSON.stringify(updated),
          );
          return updated;
        });
        return;
      }

      setFilteredOffices((prev) => {
        const updated = [...prev, id].filter((thisId) => !!thisId);
        localStorage.setItem(
          constants.localStorageKeys.persistOfficeFilter,
          JSON.stringify(updated),
        );
        return updated;
      });
    } else {
      if (id == 0) {
        setFilteredOffices([]);
        localStorage.setItem(
          constants.localStorageKeys.persistOfficeFilter,
          JSON.stringify([]),
        );
        return;
      }
      setFilteredOffices((prev) => {
        const updated = prev.filter(
          (officeId) => officeId != id && defaultOffice.value != officeId,
        );
        localStorage.setItem(
          constants.localStorageKeys.persistOfficeFilter,
          JSON.stringify(updated),
        );
        return updated;
      });
    }
  };

  const handleFilterRoles = (e, id, isChecked) => {
    if (!setFilteredRoleIds) return;
    const normalizedId = (() => {
      if (id === 0 || id === "0") return 0;
      const n = Number(id);
      return Number.isNaN(n) ? id : n;
    })();
    const checked = isChecked !== undefined ? isChecked : !e.target.checked;
    setCurrentPatientPage(1);
    if (checked) {
      if (normalizedId === 0) {
        setFilteredRoleIds(() => {
          const updated = [0];
          localStorage.setItem(
            constants.localStorageKeys.persistRoleFilter,
            JSON.stringify(updated),
          );
          return updated;
        });
        return;
      }

      setFilteredRoleIds((prev) => {
        const updated = [...prev, normalizedId].filter((thisId) => !!thisId);
        localStorage.setItem(
          constants.localStorageKeys.persistRoleFilter,
          JSON.stringify(updated),
        );
        return updated;
      });
    } else {
      if (normalizedId === 0) {
        setFilteredRoleIds([]);
        localStorage.setItem(
          constants.localStorageKeys.persistRoleFilter,
          JSON.stringify([]),
        );
        return;
      }
      setFilteredRoleIds((prev) => {
        const updated = prev.filter(
          (roleId) =>
            Number(roleId) !== Number(normalizedId) &&
            Number(roleId) !== Number(defaultRole.value),
        );
        localStorage.setItem(
          constants.localStorageKeys.persistRoleFilter,
          JSON.stringify(updated),
        );
        return updated;
      });
    }
  };
  /*-------------------------------------------------------------------*/
  //External chat creation for step 1 - the only step

  const handleConversationTitle = (e) => {
    setErrors({});
    const value = e.target.value;
    setConversationTitle(value);
  };

  const handleFindByEmail = (val) => {
    if (findByEmail !== val) {
      setShowShareApp(false);
      setErrors({});
    }
    setFindByEmail(val);
  };

  const handleSearchEmail = (e) => {
    setErrors({});
    const value = e.target.value;
    setSearchEmail(value);
    setShowShareApp(false);
  };

  const handleSearchPhone = (e) => {
    setErrors({});
    const value = parseNumber(e.target.value);
    setSearchPhone(value);
    setShowShareApp(false);
  };

  const closeExternalChatCreationModal = () => {
    setConversationTitle("");
    setFindByEmail(true);
    setSearchEmail("");
    setSearchPhone("");
    setIsNewConversationPopupOpen(false);
    setSelectedPatient(null);
    setSearchedExternalMembers([]);
    setErrors({});
    setShowShareApp(false);
  };

  const handleExternalChatCreation = async (member) => {
    if (!conversationTitle.trim().length) {
      setErrors({ externalGroupTitle: t("messenger.titleLengthError") });
      return;
    }
    if (sdk) {
      try {
        setActionInProgress(true);
        const {
          id: SU_id,
          emailId: SU_emailId,
          name,
          officeId,
          contactNumber: SU_contactNumber,
          profilePic,
        } = member;
        //////////////////////////////////////////////////////////////////////////////////
        //Check if selected user has an account in sendbird; If not then create the user
        const userListQueryParam = {
          limit: 1,
          userIdsFilter: [SU_id?.toString()],
        };
        const userListQuery =
          sdk.createApplicationUserListQuery(userListQueryParam);
        const sendbirdUser = await userListQuery.next();
        if (!sendbirdUser.length) {
          const userData = {
            user_id: SU_id?.toString(),
            nickname: name,
            profile_url: profilePic || "",
          };
          await createSendbirdUser(userData);
        } else {
          //Check if there is already any chat initiated with this selected user
          const channelListQuery =
            sdk.groupChannel.createMyGroupChannelListQuery({
              limit: 100,
              includeEmpty: true,
              includeFrozen: false,
              searchFilter: {
                query: conversationTitle.trim(),
                fields: ["channel_name"],
              },
              customTypesFilter: [
                !externalTabActive
                  ? constants.chat.internal
                  : constants.chat.external,
              ],
              hiddenChannelFilter: constants.chat.unArchived,
              userIdsFilter: {
                userIds: [SU_id?.toString()],
                includeMode: false,
                queryType: "AND",
              },
            });
          const channelList = await channelListQuery.next();
          if (channelList.length) {
            const filteredChannels = channelList.filter((chnl) => {
              return (
                chnl?.name?.trim()?.toLowerCase() ==
                conversationTitle?.trim()?.toLowerCase()
              );
            });

            if (filteredChannels?.length) {
              const foundChannel = filteredChannels.find((chnl) => {
                let { data: chnlData } = chnl;
                if (chnlData) {
                  chnlData = JSON.parse(chnlData);
                  if (chnlData?.members?.length) {
                    const idx = chnlData?.members?.findIndex((mem) => {
                      return mem.id == SU_id;
                    });
                    if (idx > -1) {
                      return true;
                    }
                  }
                }
                return false;
              });

              if (foundChannel) {
                setCurrentChannel(foundChannel);
                setActionInProgress(false);
                closeExternalChatCreationModal();
                return;
              }
            }
          }
        }
        ///////////////////////////////////////////////////////////////////////////////////
        const {
          id: CU_id,
          emailId: CU_emailId,
          contactNumber: CU_contactNumber,
          isAccountOwner: CU_isAccountOwner,
          isStaff: CU_isStaff,
        } = currentUser;
        const groupChannelParams = {};
        groupChannelParams.invitedUserIds = [SU_id.toString()];
        groupChannelParams.name = conversationTitle.trim();
        groupChannelParams.operatorUserIds = [
          CU_id?.toString(),
          SU_id?.toString(),
        ];
        groupChannelParams.customType = !externalTabActive
          ? constants.chat.internal
          : constants.chat.external;
        const data = {};
        data.members = [
          {
            id: SU_id,
            emailId: SU_emailId,
            officeName: null,
            name,
            contactNumber: SU_contactNumber,
          },
          {
            id: CU_id,
            emailId: CU_emailId,
            officeName: null,
            name: getFullName(currentUser),
            contactNumber: CU_contactNumber,
          },
        ];

        if (!CU_isAccountOwner && CU_isStaff) {
          data.accountOwner = {
            id: selectedOwner.id,
            name: getFullName(selectedOwner),
            emailId: selectedOwner.emailId,
          };
        } else if (CU_isAccountOwner && !CU_isStaff) {
          data.accountOwner = {
            id: CU_id,
            name: getFullName(currentUser),
            emailId: CU_emailId,
          };
        }

        groupChannelParams.data = JSON.stringify(data);
        const channelDetail =
          await sdk.groupChannel.createChannel(groupChannelParams);

        setCurrentChannel(channelDetail);
        updateChannelList();
        closeExternalChatCreationModal();
      } catch (err) {
        if (err?.response?.data?.message) {
          handleError(new Error(err.response?.data?.message));
        } else {
          handleError(err);
        }
      }
      setActionInProgress(false);
    }
  };

  const handleSearchExternalMembers = async () => {
    setShowShareApp(false);
    setSearchedExternalMembers([]);
    setErrors({ externalChatEmail: "" });
    if (
      findByEmail ? validateEmail(searchEmail) : validateNumber(searchPhone)
    ) {
      try {
        setLoadingExternalMembers(true);
        const searchedText = findByEmail ? searchEmail : searchPhone;
        const searchedMembers = await getExternalMembers(
          searchedText,
          selectedOwner?.id,
        );
        if (searchedMembers?.length) {
          setSearchedExternalMembers(
            searchedMembers.map((mem) => {
              const {
                name: officeName,
                id: officeId,
                contactNumber: officeContactNumber,
              } = mem.office;
              const { emailId, profilePic, contactNumber, id } = mem.user;
              return {
                id,
                officeName,
                officeId,
                name: getFullName(mem.user),
                emailId,
                profilePic,
                contactNumber: contactNumber || officeContactNumber,
              };
            }),
          );
        } else {
          setErrors({
            externalChatEmail: findByEmail
              ? t("messenger.emailNotExist")
              : t("messenger.phoneNotExist"),
          });
          setShowShareApp(true);
        }
      } catch (error) {
        handleError(error);
      }
      setLoadingExternalMembers(false);
    } else {
      setErrors({
        externalChatEmail: findByEmail
          ? t("messenger.emailValidationError")
          : t("messenger.phoneValidationError"),
      });
    }
  };

  const isOfficeFilterApplied =
    !externalTabActive && filteredOffices?.filter((id) => !!id).length > 0;

  const resetOfficePagination = () => {
    setCurrentOfficePage(1);
    setTotalOfficePages(1);
    setCurrentOfficePageForPatient(1);
    setTotalOfficePagesForPatient(1);
    setCurrentPatientPage(1);
    setTotalPatientPages(1);
  };
  const officesList = useMemo(() => {
    if (isPatient) {
      return [defaultOffice, ...officesForPatientChat];
    }
    return [defaultOffice, ...offices];
  }, [offices, officesForPatientChat, isPatient]);

  return {
    state: {
      searchPatient,
      dropdownOfficeOpen,
      dropdownChatOpen,
      isNewGroupPopupOpen,
      isCreateGroupPopupOpen,
      isNewConversationPopupOpen,
      isPatientConversationPopOpen,
      staffMembers,
      patientList,
      isPatientListLoading,
      selectedPatient,
      selectedStaff,
      offices: officesList,
      searchText,
      groupImageUrl,
      groupName,
      conversationTitle,
      findByEmail,
      searchEmail,
      searchPhone,
      isLoadingStaffList,
      errors,
      searchedExternalMembers,
      loadingExternalMembers,
      showShareApp,
      actionInProgress,
      hasMoreStaff: currentStaffPage < totalStaffPages,
      hasMoreOffices: isOfficeNavigationContext
        ? false
        : isPatient
          ? currentOfficePageForPatient < totalOfficePagesForPatient
          : currentOfficePage < totalOfficePages,
      hasMorePatientAppoinments: currentPatientPage < totalPatientPages,
      isOfficeFilterApplied,
      currentPatientPage,
      isNewConversationOfficePopupOpen,
      isCallHistoryModalOpen,
      selectedOffices,
      selectedDesignations,
      designations,
      isLoadingOfficesList,
      totalOfficePagesCommon: isPatient
        ? activeOfficesForPatientChat?.pagination?.totalPages
        : 0,
    },
    updateMethods: {
      setSelectedStaff,
      setDropdownOfficeOpen,
      setDropdownChatOpen,
      setIsNewGroupPopupOpen,
      setIsCreateGroupPopupOpen,
      setIsNewConversationPopupOpen,
      setFindByEmail,
      setIsPatientConversationPopOpen,
      setIsNewConversationOfficePopupOpen,
    },
    otherMethods: {
      toggleCallHistoryPopUp,
      toggleOffice,
      toggleChat,
      handleStepOne,
      isThisUserSelected,
      handleSelectStaff,
      handleSelectOffice,
      handleSearchText,
      closeStepOneModal,
      handleGroupImageChange,
      closeStepTwoModal,
      handleGroupName,
      handleStepTwo,
      handleConversationTitle,
      closeExternalChatCreationModal,
      handleSearchEmail,
      handleSearchPhone,
      handleExternalChatCreation,
      showArchivedChats,
      showUnarchivedChats,
      disableOtherUsers,
      handleFilterOffices,
      handleFilterRoles,
      handleSearchExternalMembers,
      handleFindByEmail,
      loadMoreStaff,
      loadMoreOffices: isPatient
        ? loadMoreOfficesForPatientChat
        : loadMoreOffices,
      addNewMemberInExistingGroup,
      loadMorePatientAppoinments,
      handleSelectPatient,
      searchPatientHandler,
      resetOfficePagination,
      selectedOfficesLabel,
      selectedRolesLabel,
      handleOfficeFilterForStaff,
      handleDesignationFilterForStaff,
      searchStaffHandler,
      resetStaffListFilterStates,
    },
  };
};

export default useChannelListHeaderState;
